import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

interface HealthStatus {
  status: 'healthy' | 'degraded' | 'down';
  database?: boolean;
  kv?: boolean;
  cache?: boolean;
  timestamp?: string;
}

interface SystemComponent {
  name: string;
  status: 'operational' | 'degraded' | 'down';
}

const StatusPage: React.FC = () => {
  const { data: health, isLoading: loading } = useQuery<HealthStatus>({
    queryKey: ['health'],
    queryFn: async () => {
      const res = await fetch('/api/health');
      if (!res.ok) throw new Error('Health check failed');
      return res.json();
    },
    refetchInterval: 30000,
  });

  const components: SystemComponent[] = [
    { name: 'API Gateway', status: health ? 'operational' : 'down' },
    { name: 'Database', status: health?.database ? 'operational' : 'down' },
    { name: 'Cache (KV)', status: health?.kv ? 'operational' : 'down' },
    { name: 'Search API', status: health?.status === 'healthy' ? 'operational' : 'degraded' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
      case 'healthy':
        return 'emerald';
      case 'degraded':
        return 'amber';
      case 'down':
        return 'red';
      default:
        return 'slate';
    }
  };

  const overallStatus = health?.status === 'healthy' ? 'All Systems Operational' :
    health?.status === 'degraded' ? 'Degraded Performance' :
      'System Issues Detected';

  return (
    <div className="bg-slate-900 min-h-screen text-slate-300 font-sans antialiased">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-24 sm:px-6 lg:px-8 text-center">
        <h1 className="text-3xl font-bold text-white mb-12">System Status</h1>

        {loading || !health ? (
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-8">
            <div className="flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
            </div>
          </div>
        ) : (
          <>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 mb-8">
              <div className="flex items-center justify-center mb-6">
                <div className={`w-16 h-16 bg-${getStatusColor(health.status)}-500/10 rounded-full flex items-center justify-center`}>
                  {health.status === 'healthy' ? (
                    <CheckCircle2 className={`w-8 h-8 text-${getStatusColor(health.status)}-500`} />
                  ) : (
                    <AlertCircle className={`w-8 h-8 text-${getStatusColor(health.status)}-500`} />
                  )}
                </div>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{overallStatus}</h2>
              <p className="text-slate-400">
                {health.status === 'healthy'
                  ? 'Core API and Data Ingestion Pipelines are healthy.'
                  : 'Some services may be experiencing issues.'}
              </p>
              {health.timestamp && (
                <p className="text-xs text-slate-500 mt-4">Last updated: {new Date(health.timestamp).toLocaleTimeString()}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              {components.map((component, idx) => {
                const color = getStatusColor(component.status);
                return (
                  <div key={idx} className="p-4 bg-slate-950 border border-slate-800 rounded-lg flex justify-between items-center">
                    <span className="font-medium text-white">{component.name}</span>
                    <span className={`text-${color}-500 text-sm font-bold flex items-center`}>
                      <span className={`w-2 h-2 bg-${color}-500 rounded-full mr-2`}></span>
                      {component.status === 'operational' ? 'Operational' :
                        component.status === 'degraded' ? 'Degraded' : 'Down'}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default StatusPage;